import { Button, Container, Grid, Tab, Tabs, Typography } from '@mui/material'
import { CardGrid, Icon, SnackBarContext } from 'components'
import { ContentCard } from 'components/card'
import { inject, observer } from 'mobx-react'
import { Program } from 'models/program'
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {  Pages } from 'routes'
import { RoutingStore } from 'stores'
import { DataStatus, DataType, ProgramType, UserType } from 'types'

export interface IProgramListProps {
  routingStore?: RoutingStore
}

const a11yProps = (index: number) => {
  return {
    id: `course-status-${index}`,
    'aria-controls': `course-statuspanel-${index}`,
  }
}

export const ProgramList = inject('routingStore')(observer((props: IProgramListProps) => {
  const { t }            = useTranslation();
  const { routingStore } = props;
  const { toast }        = React.useContext(SnackBarContext);
  const [programs,          setPrograms]          = useState<Program[]>([]);
  const [currentTab,        setCurrentTab]        = useState<number>(0);
  const [draftPrograms,     setDraftPrograms]     = useState<Program[]>([]);
  const [archivedPrograms,  setArchivedPrograms]  = useState<Program[]>([]);
  const [publishedPrograms, setPublishedPrograms] = useState<Program[]>([]);

  useEffect(() => {
    Program.get().then((p: Program[]) => {
      setPrograms(p);
    });
  }, []);

  useEffect(() => {
    if (programs) {
      setDraftPrograms(programs.filter((p) => p.status === DataStatus.Draft));
      setPublishedPrograms(programs.filter((p) => p.status == DataStatus.Published));
      setArchivedPrograms(programs.filter((p) => p.status == DataStatus.Archived));
    } else {
      setDraftPrograms([]);
      setPublishedPrograms([]);
      setArchivedPrograms([]);
    }
  }, [programs])

  const createProgram = useCallback(async () => {
    const newProgram = Program.create({
      title: t('new_program'),
      status: DataStatus.Draft,
      desc: ' ',
      type: ProgramType.Default,
    });
    try {
      await newProgram.save();
      routingStore.push(Pages.Programs, { id: '' + newProgram.id });
    } catch (err) {
      console.error('Failed to create new program', err);
      toast(t('toast_error_create_program'), 'error');
      return;
    }
  }, []);

  return (
    <Container maxWidth='xl'>
      <Grid pt={3} container>
        <Grid
          item
          xs             = {12}
          mb             = {3}
          display        = 'flex'
          justifyContent = 'space-between'
          alignItems     = 'center'
        >
          <Typography variant='h2'>{t('programs')}</Typography>
          <Button
            variant   = 'contained'
            startIcon = {<Icon iconId='add' />}
            onClick   = {createProgram}
          >
            {t('create_new_program')}
          </Button>
        </Grid>
        <Grid xs={12} mb={2} item>
          <Tabs
            value    = {currentTab}
            onChange = {(event: SyntheticEvent, newValue: number) =>
              setCurrentTab(newValue)
            }
            aria-label = 'Program status'
          >
            <Tab label={`${t('draft')} (${draftPrograms.length})`} {...a11yProps(0)} />
            <Tab label={`${t('published')} (${publishedPrograms.length})`} {...a11yProps(1)} />
            <Tab label={`${t('archived')} (${archivedPrograms.length})`} {...a11yProps(2)} />
          </Tabs>
        </Grid>
        <CardGrid>
          {currentTab === 0 &&
            draftPrograms?.map((p) => (
              <ContentCard
                key           = {p.id}
                title         = {p.title}
                type          = {DataType.Program}
                userType      = {UserType.Participant}
                description   = {p.desc}
                category      = {p.category}
                onClick       = {() =>
                  routingStore.push(Pages.Programs, { id: '' + p.id })
                }
              />
            ))}
          {currentTab === 1 &&
            publishedPrograms?.map((p) => (
              <ContentCard
                key           = {p.id}
                title         = {p.title}
                type          = {DataType.Program}
                userType      = {UserType.Participant}
                description   = {p.desc}
                category      = {p.category}
                onClick       = {() =>
                  routingStore.push(Pages.Programs, { id: '' + p.id })
                }
              />
            ))}
          {currentTab === 2 &&
            archivedPrograms?.map((p) => (
              <ContentCard
                key           = {p.id}
                title         = {p.title}
                type          = {DataType.Program}
                userType      = {UserType.Participant}
                description   = {p.desc}
                category      = {p.category}
                onClick       = {() =>
                  routingStore.push(Pages.Programs, { id: '' + p.id })
                }
              />
            ))}
        </CardGrid>
      </Grid>
    </Container>
  );
}));
